import PropTypes from 'prop-types';
import { forwardRef } from 'react';
import { NavLink as RouterLink } from 'react-router-dom';
// @mui
import { Box, Link, Tooltip } from '@mui/material';
// hooks
import useAuth from '../../../hooks/useAuth';
import useLocales from '../../../hooks/useLocales';
// guards
import RoleBasedGuard from '../../../guards/RoleBasedGuard';
//
import Iconify from '../../Iconify';
import { ListItemStyle, ListItemTextStyle, ListItemIconStyle } from './style';
import { isExternalLink } from '..';

// ----------------------------------------------------------------------

// HANDLE SHOW ITEM BY ROLE
const ListItem = forwardRef((props, ref) => (
  <RoleBasedGuard roles={props.roles}>
    <ListItemStyle {...props} ref={ref}>
      {props.children}
    </ListItemStyle>
  </RoleBasedGuard>
));

/**
 * Whether this account sees the item. Staff accounts ("user") match on their
 * job role, which the config gives as a string or a list; every other account
 * type matches on the type itself. One rule instead of the six copies of the
 * same render block this used to be — and an item that fails it renders
 * nothing, where it used to render an empty 48px row.
 */
function useItemVisible(roles) {
  const { user, type } = useAuth();
  if (type === 'user') {
    return Array.isArray(roles)
      ? roles.includes(user?.role)
      : user?.role === roles;
  }
  return roles === type;
}

NavItemRoot.propTypes = {
  active: PropTypes.bool,
  open: PropTypes.bool,
  isCollapse: PropTypes.bool,
  onOpen: PropTypes.func,
  item: PropTypes.shape({
    children: PropTypes.array,
    icon: PropTypes.any,
    info: PropTypes.any,
    path: PropTypes.string,
    title: PropTypes.string,
    disabled: PropTypes.bool
    // roles: PropTypes.arrayOf(PropTypes.string)
  })
};

export function NavItemRoot({
  item,
  isCollapse,
  open = false,
  active,
  onOpen
}) {
  const { translate } = useLocales();
  const { title, path, icon, info, children, disabled, roles } = item;
  const visible = useItemVisible(roles);

  if (!visible) return null;

  const label = translate(title);

  const renderContent = (
    <>
      {icon && (
        <ListItemIconStyle className="sb-tile">{icon}</ListItemIconStyle>
      )}
      <ListItemTextStyle
        disableTypography
        primary={label}
        isCollapse={isCollapse}
      />
      {!isCollapse && (
        <>
          {info && info}
          {children && <ArrowIcon open={open} />}
        </>
      )}
    </>
  );

  // The collapsed rail shows tiles only, so the label travels in a tooltip.
  const withTooltip = (node) =>
    isCollapse ? (
      <Tooltip title={label} placement="right" arrow>
        {node}
      </Tooltip>
    ) : (
      node
    );

  if (children) {
    return withTooltip(
      <ListItem
        onClick={onOpen}
        activeRoot={active}
        isCollapse={isCollapse}
        disabled={disabled}
        roles={roles}
      >
        {renderContent}
      </ListItem>
    );
  }

  return withTooltip(
    isExternalLink(path) ? (
      <ListItem
        component={Link}
        href={path}
        target="_blank"
        rel="noopener"
        isCollapse={isCollapse}
        disabled={disabled}
        roles={roles}
      >
        {renderContent}
      </ListItem>
    ) : (
      <ListItem
        component={RouterLink}
        to={path}
        activeRoot={active}
        isCollapse={isCollapse}
        disabled={disabled}
        roles={roles}
      >
        {renderContent}
      </ListItem>
    )
  );
}

// ----------------------------------------------------------------------

NavItemSub.propTypes = {
  active: PropTypes.bool,
  open: PropTypes.bool,
  onOpen: PropTypes.func,
  item: PropTypes.shape({
    children: PropTypes.array,
    info: PropTypes.any,
    path: PropTypes.string,
    title: PropTypes.string,
    disabled: PropTypes.bool,
    roles: PropTypes.arrayOf(PropTypes.string)
  })
};

export function NavItemSub({ item, open = false, active = false, onOpen }) {
  const { translate } = useLocales();

  const { title, path, info, children, disabled, roles } = item;

  const renderContent = (
    <>
      <DotIcon active={active} />
      <ListItemTextStyle disableTypography primary={translate(title)} />
      {info && info}
      {children && <ArrowIcon open={open} />}
    </>
  );

  if (children) {
    return (
      <ListItem
        onClick={onOpen}
        activeSub={active}
        subItem
        disabled={disabled}
        roles={roles}
      >
        {renderContent}
      </ListItem>
    );
  }

  return isExternalLink(path) ? (
    <ListItem
      component={Link}
      href={path}
      target="_blank"
      rel="noopener"
      subItem
      disabled={disabled}
      roles={roles}
    >
      {renderContent}
    </ListItem>
  ) : (
    <ListItem
      component={RouterLink}
      to={path}
      activeSub={active}
      subItem
      disabled={disabled}
      roles={roles}
    >
      {renderContent}
    </ListItem>
  );
}

// ----------------------------------------------------------------------

DotIcon.propTypes = {
  active: PropTypes.bool
};

/**
 * The sub-item marker sits ON the rail the sub-list draws down its left edge
 * (see NavList): the list positions the rail at its own left border, and the
 * dot is pulled back over it, so the tree reads as one drawn line with
 * stations on it rather than bullets beside a line.
 */
export function DotIcon({ active }) {
  return (
    <Box
      component="span"
      sx={{
        position: 'absolute',
        left: -4,
        top: '50%',
        width: 7,
        height: 7,
        borderRadius: '50%',
        transform: 'translateY(-50%)',
        // Dark fallbacks: the rail is always the cosmos, so an unresolved
        // variable must still leave a visible dot, never the light theme's ink.
        bgcolor: active
          ? 'var(--sb-beacon, #80B4ED)'
          : 'var(--sb-dot, rgba(255,255,255,0.28))',
        boxShadow: active
          ? '0 0 0 3px var(--sb-accent-tint, rgba(128,180,237,0.14)), 0 0 12px var(--sb-glow, rgba(128,180,237,0.3))'
          : 'none',
        transition: (theme) =>
          theme.transitions.create(['background-color', 'box-shadow'], {
            duration: theme.transitions.duration.shorter
          })
      }}
    />
  );
}

// ----------------------------------------------------------------------

ArrowIcon.propTypes = {
  open: PropTypes.bool
};

export function ArrowIcon({ open }) {
  return (
    <Iconify
      icon="eva:arrow-ios-forward-fill"
      sx={{
        width: 16,
        height: 16,
        ml: 1,
        flexShrink: 0,
        color: 'var(--sb-text-muted, rgba(255,255,255,0.42))',
        transition: (theme) =>
          theme.transitions.create('transform', {
            duration: theme.transitions.duration.shorter
          }),
        ...(open && { transform: 'rotate(90deg)' })
      }}
    />
  );
}
